import { BASE_PATH } from '@constants/seller-center-paths.js';
import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isDevMode } from '@utils/app-utils.js';
import { ConfigProvider as AntdConfigProvider } from 'antd';
import i18next from 'i18next';
import ReactDOM from 'react-dom/client';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import themeConfig from './antd.theme.config';
import global_en from './globals/en.json';
import global_sa from './globals/sa.json';
import ServerError from './pages/seller-center/server-error';
import { store } from './store/index.js';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [Sentry.browserTracingIntegration()],
  tracePropagationTargets: [process.env.REACT_APP_API_URL],
  tracesSampleRate: 0.0,
  enabled: !isDevMode() && !!process.env.REACT_APP_SENTRY_DSN,
});

i18next.use(initReactI18next).init({
  interpolation: { escapeValue: false },
  lng: 'en',
  fallbackLng: 'en',
  debug: false, // We can get logs in the console by setting "debug: true"
  resources: {
    en: {
      translation: global_en,
    },
    sa: {
      translation: global_sa,
    },
  },
});

export const clientQuery = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

// when a tab remains in-active for a long time and users switch internet, ChunkLoadError is thrown.
window.addEventListener('unhandledrejection', (event) => {
  const error = event?.reason;
  if (
    error &&
    (error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk'))
  ) {
    event.preventDefault();
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <Provider store={store}>
    <I18nextProvider i18n={i18next}>
      <AntdConfigProvider theme={themeConfig}>
        <BrowserRouter basename={BASE_PATH}>
          <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <QueryClientProvider client={clientQuery}>
              <Sentry.ErrorBoundary fallback={<ServerError />}>
                <App />
              </Sentry.ErrorBoundary>
            </QueryClientProvider>
          </GoogleOAuthProvider>
        </BrowserRouter>
      </AntdConfigProvider>
    </I18nextProvider>
  </Provider>
);
